import { invoke, InvocationOptions } from '@yoda/bridge';
import { Capacitor } from '@capacitor/core';
import commonPlugin from './capacitor-plugins';
import { isIOS_CSR } from '~/pages/aichat/hooks/useAppEnvironment';

/** ========= common start ========= */

enum CommonBridgeResultCode {
    Failed = 0,
    Success = 1,
}

interface CommonBridgeRes {
    result?: CommonBridgeResultCode;
    message?: string;
    businessCode?: number;
}

/** ========= common end ========= */

/** ========= business start ========= */

interface OpenWebviewParams {
    url: string;
}

interface SaveMediaToAlbumParams {
    mediaType: 'video' | 'image';
    url: string;
}

interface SetWebConfigParams {
    key: string;
    value: string;
}

interface ReportBotResult {
    botId: string;
    isReportSuccess: boolean;
}
/** ========= business end ========= */

class Bridge {
    private invokeBridge<T extends Record<string, any>>(bridgeName: string, params?: Record<string, any>, options?: InvocationOptions) {
        return invoke(...([bridgeName, params, options] as Parameters<typeof invoke>)) as unknown as CommonBridgeRes & T;
    }

    async openWebview(params: OpenWebviewParams) {
        const res = await this.invokeBridge('webview.open', params);

        return res;
    }

    async closeWebview() {
        const res = await this.invokeBridge('component.backOrCloseWeb', {
            closeWeb: true,
        });

        return res;
    }

    async saveMediaToAlbum(params: SaveMediaToAlbumParams) {
        const res = await this.invokeBridge('component.saveMediaToAlbum', params);

        return res;
    }

    async openWhatsAppStatusSaver() {
        const res = await this.invokeBridge('ug.openWhatsAppStatusSaver');

        return res;
    }

    async setWebConfig(params: SetWebConfigParams) {
        const res = await this.invokeBridge('component.setWebConfig', params);

        return res;
    }

    async openPrivatePrivacyPage() {
        if (isIOS_CSR.value && Capacitor.isNativePlatform()) {
            const res = await commonPlugin.openPrivatePrivacyPage();
            return res;
        }

        const res = await this.invokeBridge('ug.openPrivatePrivacyPage');
        return res;
    }

    async reportBotBridge(params: ReportBotResult) {
        const res = await this.invokeBridge('ug.reportBot', params);

        return res;
    }

    async selectImgFile() {
        const res = await this.invokeBridge('ug.selectImgFile');
        return res;
    }

    async setTitle(title: string) {
        const res = await this.invokeBridge('ui.setTitle', {
            title,
        });

        return res;
    }

    async getAppInfo() {
        const res = await this.invokeBridge('system.getAppInfo', {});
        return res;
    }

    async appLogin() {
        const res = await this.invokeBridge('component.login', {});
        return res;
    }

    async appLogout() {
        const res = await this.invokeBridge('component.logout', {});
        return res;
    }

    async refreshFantasayProfile() {
        const res = await this.invokeBridge('event.dispatchGlobalEvent', {
            type: 'krnProfileRefresh',
        });
        return res;
    }

    async getAndSetVoiceCallPermission(params: { write: boolean; turnOff?: boolean }) {
        const res = await this.invokeBridge('ug.userVoiceCallEnable', params);
        return res;
    }

    async getStatusBarHeight() {
        const res = await this.invokeBridge('system.getStatusBarHeight', {});
        return res;
    }

    async startAsrRecording(taskId: string) {
        const res = await this.invokeBridge('live.startAsrRecording', {
            taskId,
            asrBiz: 'Fantasay',
        });

        return res;
    }

    async cancelAsrRecording() {
        const res = await this.invokeBridge('live.cancelAsrRecording', {
            asrBiz: 'Fantasay',
        });

        return res;
    }

    async stopAsrRecording() {
        const res = await this.invokeBridge('live.stopAsrRecording', {
            asrBiz: 'Fantasay',
        });

        return res;
    }

    async registerGlobalEventListener(params: { type: string; listener: (res: any) => void }) {
        const res = await this.invokeBridge('event.addEventListener', params);
        return res;
    }

    async dispatchGlobalEvent(params: { type: string; data?: any }) {
        const res = await this.invokeBridge('event.dispatchGlobalEvent', params);
        return res;
    }
}

export const PwaBridge = new Bridge();
